import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../redux/store/hooks';
import { useFinancialPeriod } from '../../context/FinancialPeriodContext';
import {
  useCashbook,
  useCashbookSummary,
  useBillingCloseCashbook,
  useBillingOpenCashbook,
  useBulkDeleteCashbook,
  useCashbookTrashCount,
} from '../../services/httpServices/queries';
import { useSettings } from '../../services/httpServices/queries/useSettings';
import { billingService } from '../../services/httpServices/billingService';
import {
  BILLING_HISTORY_DAYS,
  billingHistoryCeiling,
  billingHistoryFloor,
  useBillingHistoryAccess,
} from '../../utils/billingAccess';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { CashbookEntryDetailsModal } from './CashbookEntryDetailsModal';
import { PageHelp } from '../../components/ui/PageHelp';
import { usePermissions } from '../../hooks/usePermissions';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DatePicker, MonthPicker } from '../../components/ui/DatePicker';
import { toast } from 'sonner';
import {
  Loader2,
  AlertCircle,
  BookOpen,
  CheckCircle,
  X,
  Landmark,
  PlusCircle,
  Eye,
  Printer,
  Trash2,
  Download,
} from 'lucide-react';

const CURRENCY = '৳';
const limit = 20;
const EXPORT_PAGE_SIZE = 200;

/**
 * Rows per printed sheet in the cashbook statement. The first sheet also holds
 * the letterhead and summary cards, so it fits fewer rows than the rest. Both
 * are kept conservative so a sheet never overflows onto an extra blank page.
 */
const PRINT_FIRST_PAGE_ROWS = 12;
const PRINT_PAGE_ROWS = 16;

function fmt(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fmtDate(value?: string | Date | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** Local calendar date as YYYY-MM-DD (avoids the UTC shift of toISOString). */
function isoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Last calendar day of a `YYYY-MM` month as `YYYY-MM-DD`. */
function lastDayOfMonth(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number);
  const last = new Date(year, month, 0).getDate();
  return `${yearMonth}-${String(last).padStart(2, '0')}`;
}

/**
 * Friendly label for the active range, recognising the common "today",
 * whole-month and whole-year selections so the list and the printout read like
 * a proper report period instead of a raw from→to pair.
 */
function describePeriod(dateFrom: string, dateTo: string): string {
  if (!dateFrom && !dateTo) return 'All time';

  const today = isoDate(new Date());
  if (dateFrom === today && dateTo === today) return `Today — ${fmtDate(today)}`;

  if (dateFrom && dateTo) {
    const from = dateFrom.slice(0, 10);
    const to = dateTo.slice(0, 10);
    const ym = from.slice(0, 7);
    if (from === `${ym}-01` && to === lastDayOfMonth(ym)) {
      const [year, month] = ym.split('-').map(Number);
      return new Date(year, month - 1, 1).toLocaleDateString('en-GB', {
        month: '2-digit',
        year: 'numeric',
      });
    }
    const year = from.slice(0, 4);
    if (from === `${year}-01-01` && to === `${year}-12-31`) return `Year ${year}`;
  }

  const from = dateFrom ? fmtDate(dateFrom) : 'Beginning';
  const to = dateTo ? fmtDate(dateTo) : 'Today';
  return `${from} → ${to}`;
}

interface CashbookPeriodPreset {
  key: 'all' | 'today' | 'week' | 'month' | 'year';
  label: string;
}

const PERIOD_PRESETS: CashbookPeriodPreset[] = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: `Last ${BILLING_HISTORY_DAYS} Days` },
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
];

function enteredByName(entry: any): string {
  const user = entry?.enteredBy;
  if (!user) return '—';
  return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
}

/** Capitalise an internal entry type for display (e.g. `opening` → `Opening`). */
function typeLabel(type?: string | null): string {
  if (!type) return '—';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

interface CashbookHospital {
  name: string;
  address?: string;
  email?: string;
  website?: string;
  hotline?: string;
  logo?: string;
}

/**
 * Build a self-contained, print-ready cashbook statement. Kept in sync with the
 * invoice print document (same teal letterhead) so every billing printout looks
 * like part of the same system.
 *
 * The entries are chunked into fixed sheets so a long statement paginates
 * predictably: each sheet repeats the column header and closes with its own
 * "Page Total" subtotal, the final sheet also carries the overall "Total", and
 * every sheet after the first opens with a top gap. The `@page { margin: 0 }`
 * rule suppresses the browser's default header/footer (the date/time and
 * document title it stamps on every page); the visual margin comes from the
 * sheet padding instead.
 */
function buildCashbookPrintHtml(params: {
  entries: any[];
  summary: { opening: number; cashIn: number; cashOut: number; balance: number };
  hospital: CashbookHospital;
  periodLabel: string;
  periodRange: string;
  generatedAt: Date;
}): string {
  const { entries, summary, hospital, periodLabel, periodRange, generatedAt } = params;

  const contact = [
    hospital.hotline ? `Hotline: ${hospital.hotline}` : '',
    hospital.email,
    hospital.website,
  ]
    .filter(Boolean)
    .join(' · ');

  const totalDebit = entries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
  const totalCredit = entries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);
  const closingBalance = totalDebit - totalCredit;

  const generatedLabel = generatedAt.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  /** One data row. */
  const renderRow = (entry: any): string => {
    const debit = Number(entry.debit) || 0;
    const credit = Number(entry.credit) || 0;
    const type = String(entry.type || '');
    return `
      <tr>
        <td>${escapeHtml(fmtDate(entry.date))}</td>
        <td><span class="badge ${escapeHtml(type)}">${escapeHtml(typeLabel(type))}</span></td>
        <td><div class="desc">${escapeHtml(entry.entryDescription || '—')}</div></td>
        <td>${escapeHtml(entry.shiftCode || '—')}</td>
        <td class="num success">${debit ? escapeHtml(fmt(debit)) : '<span class="muted">—</span>'}</td>
        <td class="num danger">${credit ? escapeHtml(fmt(credit)) : '<span class="muted">—</span>'}</td>
        <td class="num strong">${escapeHtml(fmt(entry.runningBalance))}</td>
      </tr>`;
  };

  /** Debit/credit sums for a single sheet, plus its closing running balance. */
  const sheetTotals = (sheetEntries: any[]) => {
    const debit = sheetEntries.reduce((sum, e) => sum + (Number(e.debit) || 0), 0);
    const credit = sheetEntries.reduce((sum, e) => sum + (Number(e.credit) || 0), 0);
    const last = sheetEntries[sheetEntries.length - 1];
    const balance =
      last && last.runningBalance != null ? Number(last.runningBalance) || 0 : debit - credit;
    return { debit, credit, balance };
  };

  // Split the entries into fixed sheets so every printed page can carry its own
  // subtotal. The first sheet is shorter because it also holds the letterhead
  // and the summary cards.
  const sheets: any[][] = [];
  if (entries.length === 0) {
    sheets.push([]);
  } else {
    sheets.push(entries.slice(0, PRINT_FIRST_PAGE_ROWS));
    for (let i = PRINT_FIRST_PAGE_ROWS; i < entries.length; i += PRINT_PAGE_ROWS) {
      sheets.push(entries.slice(i, i + PRINT_PAGE_ROWS));
    }
  }
  const multiPage = sheets.length > 1;

  const tableHead = `
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>Description</th>
            <th>Shift</th>
            <th class="num">Debit</th>
            <th class="num">Credit</th>
            <th class="num">Balance</th>
          </tr>
        </thead>`;

  // The letterhead and summary cards are printed once, on the first sheet only.
  const headerBlock = `
    <div class="header">
      <div class="brand">
        ${hospital.logo
          ? `<img class="logo" src="${escapeHtml(hospital.logo)}" alt="logo" />`
          : `<div class="mark">${escapeHtml((hospital.name || 'H').trim().charAt(0).toUpperCase())}</div>`}
        <div>
          <h1>${escapeHtml(hospital.name)}</h1>
          ${hospital.address ? `<div class="sub">${escapeHtml(hospital.address)}</div>` : ''}
          ${contact ? `<div class="sub">${escapeHtml(contact)}</div>` : ''}
        </div>
      </div>
      <div class="title">
        <div class="label">Cashbook Statement</div>
        <div class="period"><b>Period:</b> ${escapeHtml(periodLabel)}</div>
        <div class="period"><b>Dates:</b> ${escapeHtml(periodRange)}</div>
        <div class="period"><b>Entries:</b> ${entries.length}</div>
      </div>
    </div>

    <div class="cards">
      <div class="card"><span>Opening Balance</span><strong>${escapeHtml(fmt(summary.opening))}</strong></div>
      <div class="card in"><span>Cash In</span><strong>${escapeHtml(fmt(summary.cashIn))}</strong></div>
      <div class="card out"><span>Cash Out</span><strong>${escapeHtml(fmt(summary.cashOut))}</strong></div>
      <div class="card net"><span>Net Balance</span><strong>${escapeHtml(fmt(summary.balance))}</strong></div>
    </div>`;

  const sheetBlocks = sheets
    .map((sheetEntries, index) => {
      const isFirst = index === 0;
      const isLast = index === sheets.length - 1;
      const { debit, credit, balance } = sheetTotals(sheetEntries);

      const body = sheetEntries.length
        ? sheetEntries.map(renderRow).join('')
        : '<tr><td colspan="7" class="muted" style="text-align:center;padding:28px;">No cashbook entries for the selected period</td></tr>';

      // A "Page Total" subtotal closes every sheet in a multi-page statement;
      // the overall "Total" closes the statement on its final sheet.
      const pageTotalRow =
        multiPage && sheetEntries.length
          ? `<tr class="total">
              <td colspan="4">Page ${index + 1} Total</td>
              <td class="num success">${escapeHtml(fmt(debit))}</td>
              <td class="num danger">${escapeHtml(fmt(credit))}</td>
              <td class="num">${escapeHtml(fmt(balance))}</td>
            </tr>`
          : '';

      const grandTotalRow = isLast
        ? `<tr class="grand">
              <td colspan="4">Total</td>
              <td class="num success">${escapeHtml(fmt(totalDebit))}</td>
              <td class="num danger">${escapeHtml(fmt(totalCredit))}</td>
              <td class="num">${escapeHtml(fmt(closingBalance))}</td>
            </tr>`
        : '';

      return `
    <section class="sheet${isFirst ? ' first-sheet' : ''}">
      ${isFirst ? headerBlock : ''}
      <table>
        ${tableHead}
        <tbody>${body}</tbody>
        <tfoot>${pageTotalRow}${grandTotalRow}</tfoot>
      </table>
      ${isLast ? `<div class="footer">Printed on ${escapeHtml(generatedLabel)}${hospital.name ? ` · ${escapeHtml(hospital.name)}` : ''}</div>` : ''}
    </section>`;
    })
    .join('');

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Cashbook Statement</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #0f172a; margin: 0; padding: 0; background: #ffffff; font-size: 12px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .doc { max-width: 900px; margin: 0 auto; }

  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; padding-bottom: 16px; border-bottom: 3px solid #0f766e; }
  .brand { display: flex; align-items: flex-start; gap: 12px; min-width: 0; }
  .brand .logo { height: 52px; width: 52px; border-radius: 12px; object-fit: contain; }
  .brand .mark { height: 52px; width: 52px; border-radius: 12px; background: rgba(15,118,110,.1); color: #0f766e; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 800; }
  .brand h1 { margin: 0; font-size: 20px; font-weight: 800; color: #0f172a; }
  .brand .sub { font-size: 11px; color: #64748b; margin-top: 3px; line-height: 1.5; }
  .title { text-align: right; white-space: nowrap; }
  .title .label { font-size: 19px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: #0f766e; }
  .title .period { margin-top: 6px; font-size: 11px; color: #475569; }
  .title .period b { color: #0f172a; }

  .cards { display: flex; gap: 12px; margin-top: 20px; }
  .card { flex: 1; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; }
  .card span { display: block; font-size: 9px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #64748b; }
  .card strong { display: block; margin-top: 6px; font-size: 17px; font-weight: 800; color: #0f172a; }
  .card.in { border-color: #bbf7d0; background: #f0fdf4; }
  .card.in strong { color: #15803d; }
  .card.out { border-color: #fecaca; background: #fef2f2; }
  .card.out strong { color: #b91c1c; }
  .card.net { border-color: #99f6e4; background: #f0fdfa; }
  .card.net strong { color: #0f766e; }

  table { width: 100%; border-collapse: collapse; margin-top: 22px; }
  thead th { background: #0f766e; color: #ffffff; text-align: left; padding: 9px 10px; font-size: 10px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; }
  thead th:first-child { border-top-left-radius: 8px; }
  thead th:last-child { border-top-right-radius: 8px; }
  tbody td { padding: 9px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  td .desc { max-width: 320px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
  tfoot td { padding: 11px 10px; font-size: 12px; }
  tfoot tr.total td { border-top: 2px solid #0f766e; background: #f7faf9; font-weight: 800; }
  tfoot tr.grand td { border-top: 2px solid #0f172a; background: #f0fdfa; font-weight: 800; font-size: 13px; }
  .num { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
  .strong { font-weight: 700; }
  .success { color: #15803d; }
  .danger { color: #b91c1c; }
  .muted { color: #94a3b8; }
  .badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: capitalize; background: #f1f5f9; color: #334155; }
  .badge.opening { background: #dcfce7; color: #15803d; }
  .badge.closing { background: #fee2e2; color: #b91c1c; }
  .badge.adjustment { background: #fef3c7; color: #b45309; }
  .badge.refund { background: #fef3c7; color: #b45309; }

  .footer { margin-top: 28px; border-top: 1px solid #e2e8f0; padding-top: 10px; font-size: 10px; color: #94a3b8; text-align: center; line-height: 1.6; }

  /* One printable sheet per section. Every sheet past the first repeats the
     table header, opens with a top gap and closes with its own subtotal. */
  .sheet { padding: 28px 32px; }
  .sheet + .sheet { padding-top: 56px; border-top: 8px solid #f1f5f9; }
  .sheet table { margin-top: 0; }
  .sheet.first-sheet table { margin-top: 22px; }
  .sheet { page-break-after: always; break-after: page; }
  .sheet:last-child { page-break-after: auto; break-after: auto; }

  /* Zero the @page margin so the browser does not print its default
     header/footer (URL, date, page count); spacing comes from the sheet
     padding. Sheets after the first get an extra top gap. */
  @media print {
    @page { margin: 0; }
    body { padding: 0; }
    .doc { max-width: none; }
    .sheet { padding: 14mm 12mm; }
    .sheet + .sheet { padding-top: 26mm; border-top: 0; }
  }
</style>
</head>
<body>
  <div class="doc">${sheetBlocks}
  </div>
</body>
</html>`;
}

/** Serialise the cashbook rows to a spreadsheet-friendly CSV string. */
function buildCashbookCsv(entries: any[]): string {
  const header = [
    'Date',
    'Type',
    'Description',
    'Shift',
    'Debit',
    'Credit',
    'Balance',
    'Entered By',
    'Reference',
  ];
  const cell = (value: unknown) => {
    const text = String(value ?? '');
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const rows = entries.map((entry: any) => [
    entry.date ? new Date(entry.date).toISOString().slice(0, 10) : '',
    entry.type || '',
    entry.entryDescription || '',
    entry.shiftCode || '',
    Number(entry.debit) || 0,
    Number(entry.credit) || 0,
    Number(entry.runningBalance) || 0,
    enteredByName(entry),
    entry.referenceType || '',
  ]);
  return [header, ...rows].map((row) => row.map(cell).join(',')).join('\r\n');
}

function downloadCsv(filename: string, csv: string): void {
  // Prepend a BOM so Excel opens the Bangla currency and UTF-8 text correctly.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function BillingCashbook() {
  const { user } = useAppSelector((state) => state.auth);
  const { hasAccess } = usePermissions();
  // Written-off receivables are shown next to the cash position only for roles
  // that can open the Risk Fund ledger (explicit `billing:risk-fund:*` grant).
  const canViewRiskFund = hasAccess('/billing/risk-fund');
  const { unrestricted, clamp } = useBillingHistoryAccess();
  /*
   * The cashbook shares the app-wide financial period with Billing, the Top
   * Sheet and the dashboard (see FinancialPeriodContext). It used to own local
   * date state that always opened on "today", so narrowing the Top Sheet to a
   * month left the Cashbook on a different window and the two "balances" never
   * agreed. One period → one set of numbers.
   */
  const { period, setPeriod } = useFinancialPeriod();
  const dateFrom = period.from;
  const dateTo = period.to;
  const [page, setPage] = useState(1);

  /** Replace the whole range in one update (never two stale partial updates). */
  const updateRange = useCallback(
    (from: string, to: string) => setPeriod({ from, to }),
    [setPeriod],
  );
  const setDateFrom = useCallback(
    (value: string) => setPeriod({ ...period, from: value }),
    [period, setPeriod],
  );
  const setDateTo = useCallback(
    (value: string) => setPeriod({ ...period, to: value }),
    [period, setPeriod],
  );

  // Non-admin roles are capped at the last seven days. Normalise any stale
  // persisted range back into the allowed window.
  useEffect(() => {
    if (unrestricted) return;
    const safe = clamp({ from: dateFrom, to: dateTo });
    if (safe.from !== dateFrom || safe.to !== dateTo) {
      updateRange(safe.from, safe.to);
    }
  }, [dateFrom, dateTo, unrestricted, clamp, updateRange]);

  const { data: cashbookData, isLoading, error } = useCashbook({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    limit,
  });
  const { data: summaryData } = useCashbookSummary({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });
  /*
   * Open/Close always act on TODAY (the backend posts them to the current date),
   * so the modal defaults come from a dedicated today summary rather than from
   * whichever period the page happens to be showing.
   */
  const todayKey = useMemo(() => isoDate(new Date()), []);
  const { data: todaySummaryData } = useCashbookSummary({
    dateFrom: todayKey,
    dateTo: todayKey,
  });
  // Today's rows tell us whether the day was already opened/closed, so the
  // buttons can't fire an action the backend will reject.
  const { data: todayEntriesData } = useCashbook({
    dateFrom: todayKey,
    dateTo: todayKey,
    page: 1,
    limit: 100,
  });
  const todayEntries: any[] = todayEntriesData?.data || [];
  const openedToday = todayEntries.some((entry) => entry.type === 'opening');
  const closedToday = todayEntries.some((entry) => entry.type === 'closing');
  const todaySummary = todaySummaryData?.data;
  const todayOpening = Number(todaySummary?.opening) || 0;
  const todayExpectedBalance = Number(todaySummary?.balance) || 0;
  // Physical cash cannot be negative. A negative book balance means expenses
  // were recorded without an opening float; default the count to 0 and let the
  // cashier enter what is actually in the drawer.
  const openingDefault = Math.max(0, todayOpening);
  const closingDefault = Math.max(0, todayExpectedBalance);
  const { data: trashCountData } = useCashbookTrashCount();

  const cashbook = useMemo(() => cashbookData?.data || [], [cashbookData]);
  const meta = cashbookData?.meta;
  const trashCount = Number(trashCountData?.data?.count ?? 0);

  const [viewing, setViewing] = useState<any | null>(null);

  // Row selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState({ openingBalance: '', shiftCode: 'MORNING' });
  const [closeForm, setCloseForm] = useState({ closingBalance: '', shiftCode: 'MORNING', varianceReason: '' });

  const openCashbook = useBillingOpenCashbook();
  const closeCashbook = useBillingCloseCashbook();
  const bulkDeleteCashbook = useBulkDeleteCashbook();
  const { data: settingsData } = useSettings();

  // 'print' | 'csv' while an export is being assembled, so the buttons can show
  // a spinner instead of letting the user queue the same request twice.
  const [busy, setBusy] = useState<'print' | 'csv' | null>(null);

  // Reset to the first page whenever the period filter changes.
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo]);

  const allSelected = cashbook.length > 0 && cashbook.every((entry: any) => selectedIds.has(entry.id));
  const someSelected = cashbook.some((entry: any) => selectedIds.has(entry.id));

  // Drop selected ids that are no longer visible (e.g. after paging/filtering).
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(cashbook.map((entry: any) => entry.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [cashbook]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = cashbook.length > 0 && cashbook.every((entry: any) => prev.has(entry.id));
      return everySelected ? new Set() : new Set(cashbook.map((entry: any) => entry.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await bulkDeleteCashbook.mutateAsync(ids);
      const result = (res as any)?.data ?? {};
      const deletedCount = result.deleted?.length ?? ids.length;
      setSelectedIds(new Set());
      toast.success(`Moved ${deletedCount} entr${deletedCount === 1 ? 'y' : 'ies'} to trash`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete selected entries');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  const summary = summaryData?.data || { opening: 0, cashIn: 0, cashOut: 0, balance: 0 };

  // Hospital branding for the printed statement, mirroring the invoice print.
  const hospital = useMemo<CashbookHospital>(() => {
    const find = (key: string) =>
      settingsData?.data?.find((s: any) => s.key === key)?.value?.trim() || '';
    return {
      name: find('hospital_name_en') || find('hospital_name') || 'Hospital',
      address: find('hospital_address'),
      email: find('hospital_email'),
      website: find('hospital_website'),
      hotline: find('hospital_hotline'),
      logo: find('hospital_logo'),
    };
  }, [settingsData]);

  const periodLabel = useMemo(() => describePeriod(dateFrom, dateTo), [dateFrom, dateTo]);

  const periodRange = useMemo(() => {
    const from = dateFrom ? fmtDate(dateFrom) : 'Beginning';
    const to = dateTo ? fmtDate(dateTo) : 'Today';
    return `${from} → ${to}`;
  }, [dateFrom, dateTo]);

  // The month/year pickers are pure views over the from/to range: they only show
  // a value when the range is exactly a whole month or a whole year, so they
  // stay in sync no matter which control set the range.
  const monthValue = useMemo(() => {
    if (!dateFrom || !dateTo) return '';
    const from = dateFrom.slice(0, 10);
    const to = dateTo.slice(0, 10);
    const ym = from.slice(0, 7);
    return from === `${ym}-01` && to === lastDayOfMonth(ym) ? ym : '';
  }, [dateFrom, dateTo]);

  const yearValue = useMemo(() => {
    if (!dateFrom || !dateTo) return '';
    const year = dateFrom.slice(0, 4);
    return dateFrom.slice(0, 10) === `${year}-01-01` && dateTo.slice(0, 10) === `${year}-12-31`
      ? year
      : '';
  }, [dateFrom, dateTo]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 16 }, (_, index) => current - index);
  }, []);

  const applyPreset = (preset: CashbookPeriodPreset['key']) => {
    if (preset === 'all') {
      updateRange('', '');
      return;
    }
    const now = new Date();
    if (preset === 'today') {
      const today = isoDate(now);
      updateRange(today, today);
      return;
    }
    if (preset === 'week') {
      updateRange(billingHistoryFloor(now), billingHistoryCeiling(now));
      return;
    }
    if (preset === 'month') {
      const ym = isoDate(now).slice(0, 7);
      updateRange(`${ym}-01`, lastDayOfMonth(ym));
      return;
    }
    const year = String(now.getFullYear());
    updateRange(`${year}-01-01`, `${year}-12-31`);
  };

  const isPresetActive = (preset: CashbookPeriodPreset['key']): boolean => {
    if (preset === 'all') return !dateFrom && !dateTo;
    const now = new Date();
    if (preset === 'today') {
      const today = isoDate(now);
      return dateFrom === today && dateTo === today;
    }
    if (preset === 'week') {
      return dateFrom === billingHistoryFloor(now) && dateTo === billingHistoryCeiling(now);
    }
    if (preset === 'month') {
      const ym = isoDate(now).slice(0, 7);
      return dateFrom === `${ym}-01` && dateTo === lastDayOfMonth(ym);
    }
    const year = String(now.getFullYear());
    return dateFrom === `${year}-01-01` && dateTo === `${year}-12-31`;
  };

  const handleMonthChange = (yearMonth: string) => {
    if (!yearMonth) {
      updateRange('', '');
      return;
    }
    updateRange(`${yearMonth}-01`, lastDayOfMonth(yearMonth));
  };

  const handleYearChange = (year: string) => {
    if (!year) {
      updateRange('', '');
      return;
    }
    updateRange(`${year}-01-01`, `${year}-12-31`);
  };

  /**
   * Pull every entry for the active period (not just the visible page) so the
   * printed statement and the CSV export cover the whole filtered range and the
   * totals row is meaningful.
   */
  const fetchAllEntries = async (): Promise<any[]> => {
    const filters = { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };
    const first: any = await billingService.getCashbook({ ...filters, page: 1, limit: EXPORT_PAGE_SIZE });
    let entries: any[] = first?.data || [];
    const total = Number(first?.meta?.total ?? entries.length);
    const totalPages = Math.max(1, Math.ceil(total / EXPORT_PAGE_SIZE));
    for (let nextPage = 2; nextPage <= totalPages; nextPage += 1) {
      const res: any = await billingService.getCashbook({ ...filters, page: nextPage, limit: EXPORT_PAGE_SIZE });
      entries = entries.concat(res?.data || []);
    }
    return entries;
  };

  const resetModals = () => {
    setShowOpenModal(false);
    setShowCloseModal(false);
    setModalError(null);
    setOpenForm({ openingBalance: '', shiftCode: 'MORNING' });
    setCloseForm({ closingBalance: '', shiftCode: 'MORNING', varianceReason: '' });
  };

  const handleOpen = (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    if (!openForm.openingBalance) {
      setModalError('Please enter the opening balance');
      return;
    }
    openCashbook.mutate(
      {
        openingBalance: parseFloat(openForm.openingBalance),
        enteredById: user?.id || '',
        shiftCode: openForm.shiftCode,
      },
      {
        onSuccess: () => resetModals(),
        onError: (err: any) => setModalError(err?.message || 'Failed to open cashbook'),
      },
    );
  };

  const handleClose = (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    if (!closeForm.closingBalance) {
      setModalError('Please enter the closing balance');
      return;
    }
    closeCashbook.mutate(
      {
        closingBalance: parseFloat(closeForm.closingBalance),
        enteredById: user?.id || '',
        shiftCode: closeForm.shiftCode,
        varianceReason: closeForm.varianceReason,
      },
      {
        onSuccess: () => resetModals(),
        onError: (err: any) => setModalError(err?.message || 'Failed to close cashbook'),
      },
    );
  };

  const variance = closeForm.closingBalance
    ? parseFloat(closeForm.closingBalance) - todayExpectedBalance
    : 0;

  const handlePrint = async () => {
    setBusy('print');
    try {
      const entries = await fetchAllEntries();
      const html = buildCashbookPrintHtml({
        entries,
        summary,
        hospital,
        periodLabel,
        periodRange,
        generatedAt: new Date(),
      });
      const win = window.open('', '_blank', 'width=900,height=800');
      if (!win) {
        toast.error('Please allow pop-ups to print the cashbook');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      win.focus();
      win.onafterprint = () => win.close();
      setTimeout(() => {
        win.focus();
        win.print();
      }, 350);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to prepare the print preview');
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadCsv = async () => {
    setBusy('csv');
    try {
      const entries = await fetchAllEntries();
      if (entries.length === 0) {
        toast.info('No cashbook entries to export for this period');
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const name =
        dateFrom || dateTo
          ? `cashbook_${dateFrom || 'start'}_to_${dateTo || stamp}.csv`
          : `cashbook_all-time_${stamp}.csv`;
      downloadCsv(name, buildCashbookCsv(entries));
      toast.success(`Exported ${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} to CSV`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to export the cashbook');
    } finally {
      setBusy(null);
    }
  };

  const summaryCards = [
    { label: 'Opening Balance', value: fmt(summary.opening), icon: Landmark, color: 'bg-primary/10 text-primary' },
    { label: 'Cash In', value: fmt(summary.cashIn), icon: PlusCircle, color: 'bg-success/10 text-success' },
    { label: 'Cash Out', value: fmt(summary.cashOut), icon: CheckCircle, color: 'bg-danger/10 text-danger' },
    { label: 'Net Balance', value: fmt(summary.balance), icon: BookOpen, color: 'bg-primary/10 text-primary' },
    ...(canViewRiskFund
      ? [
          { label: 'OPD Risk Fund', value: fmt((summary as any).riskFundOpd), icon: AlertCircle, color: 'bg-warning/10 text-warning' },
          { label: 'IPD Risk Fund', value: fmt((summary as any).riskFundIpd), icon: AlertCircle, color: 'bg-danger/10 text-danger' },
        ]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-primary" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-text">Cashbook</h1>
              <PageHelp path="/billing/cashbook" />
            </div>
            <p className="text-xs text-text-muted">Daily cash movement and counter reconciliation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/billing/cashbook/trash"
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trashed cashbook entries"
          >
            <Trash2 size={16} />
            Trash
            {trashCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                {trashCount > 99 ? '99+' : trashCount}
              </span>
            )}
          </Link>
          <button
            onClick={handlePrint}
            disabled={busy !== null}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy === 'print' ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            Print
          </button>
          <button
            onClick={handleDownloadCsv}
            disabled={busy !== null}
            title="Download the entries for the selected period as CSV"
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy === 'csv' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            CSV
          </button>
          <button
            onClick={() => {
              setShowOpenModal(true);
              setModalError(null);
              // Auto-fill with the balance carried from previous days. The
              // cashier only edits it when the physical count differs.
              setOpenForm({
                openingBalance: String(openingDefault),
                shiftCode: 'MORNING',
              });
            }}
            disabled={openedToday}
            title={openedToday ? 'Cashbook is already open for today' : 'Open today and confirm the opening cash count'}
            className="px-4 py-2 bg-surface border border-border text-text rounded-lg hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusCircle size={16} />
            {openedToday ? 'Day Opened' : 'Open Day'}
          </button>
          <button
            onClick={() => {
              setShowCloseModal(true);
              setModalError(null);
              // Auto-fill with the expected drawer balance ("count what the
              // book says"; change it only to record a shortage/overage).
              setCloseForm({
                closingBalance: String(closingDefault),
                shiftCode: 'MORNING',
                varianceReason: '',
              });
            }}
            disabled={closedToday}
            title={closedToday ? 'Cashbook is already closed for today' : 'Close today and confirm the closing cash count'}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle size={16} />
            {closedToday ? 'Day Closed' : 'Close Day'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4 space-y-4">
        {/* Quick period presets */}
        <div className="flex flex-wrap items-center gap-2">
          {PERIOD_PRESETS.filter(
            (preset) => unrestricted || preset.key === 'today' || preset.key === 'week',
          ).map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => applyPreset(preset.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                isPresetActive(preset.key)
                  ? 'bg-primary text-white border-primary'
                  : 'bg-background text-text-muted border-border hover:text-text hover:border-primary/40'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Only accounts with full-history access (admin/super admin or a
            custom role holding `billing:history:full`) may pick an arbitrary
            range. Other roles are limited to the seven-day presets above. */}
        {unrestricted && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Date From</label>
              <DatePicker value={dateFrom} onChange={setDateFrom} />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Date To</label>
              <DatePicker value={dateTo} onChange={setDateTo} />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Month</label>
              <MonthPicker value={monthValue} onChange={handleMonthChange} />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1 uppercase">Year</label>
              <select
                value={yearValue}
                onChange={(e) => handleYearChange(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="">All years</option>
                {yearOptions.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-text-muted">
            Showing <span className="font-medium text-text">{periodLabel}</span>
            {!unrestricted && ` (limited to the last ${BILLING_HISTORY_DAYS} days)`}
          </p>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                if (unrestricted) {
                  updateRange('', '');
                } else {
                  updateRange(billingHistoryFloor(), billingHistoryCeiling());
                }
              }}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors flex items-center gap-1.5"
            >
              <X size={14} />
              {unrestricted ? 'Clear' : 'Reset'}
            </button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <div key={card.label} className="bg-surface rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-muted uppercase">{card.label}</p>
                <p className="text-xl font-bold text-text mt-1">{card.value}</p>
              </div>
              <div className={`p-2.5 rounded-lg ${card.color}`}>
                <card.icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> entr{selectedIds.size === 1 ? 'y' : 'ies'} selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteCashbook.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteCashbook.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteCashbook.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Cashbook Entries */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load cashbook</span>
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={cashbook.length === 0}
                      title="Select all"
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Shift</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Credit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Balance</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {cashbook.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                      No cashbook entries
                    </td>
                  </tr>
                ) : (
                  cashbook.map((entry: any) => (
                    <tr key={entry.id} className="hover:bg-background/50 transition-colors">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(entry.id)}
                          onChange={() => toggleOne(entry.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-text">{new Date(entry.date).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            entry.type === 'opening'
                              ? 'bg-success/10 text-success'
                              : entry.type === 'closing'
                              ? 'bg-danger/10 text-danger'
                              : entry.type === 'adjustment'
                              ? 'bg-warning/10 text-warning'
                              : entry.type === 'refund'
                              ? 'bg-warning/10 text-warning'
                              : 'bg-primary/10 text-primary'
                          }`}
                        >
                          {typeLabel(entry.type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text max-w-xs truncate">
                        <button
                          type="button"
                          onClick={() => setViewing(entry)}
                          className="hover:text-primary hover:underline transition-colors text-left truncate max-w-xs"
                          title="View entry details"
                        >
                          {entry.entryDescription}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-xs text-text-muted">{entry.shiftCode || '—'}</td>
                      <td className="px-4 py-3 text-sm text-success text-right">
                        {entry.debit ? fmt(entry.debit) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-danger text-right">
                        {entry.credit ? fmt(entry.credit) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-text text-right">
                        {fmt(entry.runningBalance)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setViewing(entry)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={limit}
            onPageChange={setPage}
            itemName="entries"
          />
        </div>
      )}

      {/* Cashbook entry details modal */}
      <CashbookEntryDetailsModal open={viewing !== null} entry={viewing} onClose={() => setViewing(null)} />

      {/* Open Cashbook Modal */}
      {showOpenModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">Open Cashbook</h2>
              <button onClick={resetModals} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            {modalError && (
              <div className="flex items-start gap-2 p-4 bg-danger/10 border-b border-danger/20 text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleOpen} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Shift</label>
                <select
                  value={openForm.shiftCode}
                  onChange={(e) => setOpenForm({ ...openForm, shiftCode: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="MORNING">Morning</option>
                  <option value="AFTERNOON">Afternoon</option>
                  <option value="NIGHT">Night</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Opening Balance *</label>
                <ValidatedNumberInput
                  value={openForm.openingBalance}
                  onChange={(v) => setOpenForm({ ...openForm, openingBalance: v ? String(v) : '' })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-[11px] text-text-muted mt-1">
                  Pre-filled with the balance carried from previous days
                  ({fmt(openingDefault)}). Edit it only if the physical cash
                  count is different — the difference is posted as an adjustment.
                  {todayOpening < 0 && (
                    <span className="block mt-1 text-warning">
                      The book balance is negative ({fmt(todayOpening)}): earlier
                      expenses were recorded without an opening float. Enter the
                      cash actually in the drawer to correct it.
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                <button type="button" onClick={resetModals} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={openCashbook.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {openCashbook.isPending ? <Loader2 size={16} className="animate-spin" /> : <PlusCircle size={16} />}
                  Open Cashbook
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Close Cashbook Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text">Close Cashbook</h2>
              <button onClick={resetModals} className="p-1 hover:bg-background rounded-lg transition-colors">
                <X size={18} className="text-text-muted" />
              </button>
            </div>

            {modalError && (
              <div className="flex items-start gap-2 p-4 bg-danger/10 border-b border-danger/20 text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{modalError}</span>
              </div>
            )}

            <form onSubmit={handleClose} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Shift</label>
                <select
                  value={closeForm.shiftCode}
                  onChange={(e) => setCloseForm({ ...closeForm, shiftCode: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="MORNING">Morning</option>
                  <option value="AFTERNOON">Afternoon</option>
                  <option value="NIGHT">Night</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Closing Balance *</label>
                <ValidatedNumberInput
                  value={closeForm.closingBalance}
                  onChange={(v) => setCloseForm({ ...closeForm, closingBalance: v ? String(v) : '' })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-[11px] text-text-muted mt-1">
                  Pre-filled with the expected drawer balance. Edit it to record
                  the counted cash; any shortage/overage is posted as an
                  adjustment so tomorrow starts from the real count.
                </p>
              </div>
              <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-text space-y-1">
                <p className="font-medium">Expected balance: {fmt(todayExpectedBalance)}</p>
                {closeForm.closingBalance && (
                  <p className={variance === 0 ? 'text-success' : 'text-danger'}>
                    Variance: {fmt(variance)} {variance === 0 ? '(balanced)' : '(variance flagged)'}
                  </p>
                )}
                {todayExpectedBalance < 0 && (
                  <p className="text-[11px] text-warning">
                    The book balance is negative because earlier expenses were
                    recorded without an opening float. Enter the cash actually
                    counted; the difference will be posted as an adjustment.
                  </p>
                )}
              </div>
              {closeForm.closingBalance && variance !== 0 && (
                <div>
                  <label className="block text-sm font-medium text-text mb-1">Variance Reason</label>
                  <input
                    type="text"
                    value={closeForm.varianceReason}
                    onChange={(e) => setCloseForm({ ...closeForm, varianceReason: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Explain the difference"
                  />
                </div>
              )}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-border">
                <button type="button" onClick={resetModals} className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={closeCashbook.isPending}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {closeCashbook.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  Close Cashbook
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Move ${selectedIds.size} entr${selectedIds.size === 1 ? 'y' : 'ies'} to trash?`}
        description="This moves the selected cashbook entries to trash and recomputes the affected daily balances. You can restore or permanently delete them later."
        confirmLabel={`Move ${selectedIds.size} entr${selectedIds.size === 1 ? 'y' : 'ies'} to trash`}
        loading={bulkDeleteCashbook.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
